import type { ReactNode } from 'react'

/** Minimal markdown for release notes (changelog/README.md's format):
 *  `#`–`####` headings, `-`/`*` bullet lists, paragraphs, and inline
 *  **bold**, *italic*, `code` and [links](https://…). Renders React
 *  elements only — never raw HTML — so a changelog file can't inject markup. */
export function Markdown({ source }: { source: string }) {
  const blocks: ReactNode[] = []
  let list: string[] = []
  let para: string[] = []

  const flushList = () => {
    if (list.length === 0) return
    blocks.push(
      <ul key={blocks.length} className="ml-4 list-disc space-y-1">
        {list.map((item, i) => (
          <li key={i}>{inline(item)}</li>
        ))}
      </ul>,
    )
    list = []
  }
  const flushPara = () => {
    if (para.length === 0) return
    blocks.push(<p key={blocks.length}>{inline(para.join(' '))}</p>)
    para = []
  }

  for (const raw of source.split('\n')) {
    const line = raw.trimEnd()
    const heading = /^(#{1,4})\s+(.*)$/.exec(line)
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line)
    if (heading) {
      flushList()
      flushPara()
      blocks.push(
        <h3 key={blocks.length} className="pt-1 text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold">
          {inline(heading[2])}
        </h3>,
      )
    } else if (bullet) {
      flushPara()
      list.push(bullet[1])
    } else if (line.trim() === '') {
      flushList()
      flushPara()
    } else if (list.length > 0 && /^\s+/.test(raw)) {
      list[list.length - 1] += ` ${line.trim()}` // wrapped bullet continuation
    } else {
      flushList()
      para.push(line.trim())
    }
  }
  flushList()
  flushPara()
  return <div className="space-y-2 text-sm leading-relaxed text-ostrel-ink">{blocks}</div>
}

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*\s][^*]*\*)/g

function inline(text: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={i} className="rounded-sm bg-ostrel-sand px-1 font-mono text-[0.85em]">
          {part.slice(1, -1)}
        </code>
      )
    }
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part)
    if (link) {
      const href = /^https?:\/\//.test(link[2]) ? link[2] : undefined
      return href ? (
        <a key={i} href={href} target="_blank" rel="noreferrer" className="text-ostrel-info underline">
          {link[1]}
        </a>
      ) : (
        link[1]
      )
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={i}>{part.slice(1, -1)}</em>
    }
    return part
  })
}
